<template>
  <v-dialog
    v-model="dialog"
    max-width="420"
    persistent
    content-class="disposition-dialog"
    transition="dialog-bottom-transition"
    overlay-opacity="0.5"
  >
    <v-card
      class="disposition-card"
      :class="{ 'incoming-call': lastCallDirection === 'incoming' }"
    >
      <!-- Animated Background Gradient -->
      <div
        class="disposition-bg-gradient"
        :class="lastCallDirection === 'incoming' ? 'incoming' : 'outgoing'"
      ></div>

      <!-- Header Section -->
      <div class="disposition-header">
        <div
          class="header-icon-container"
          :class="lastCallDirection === 'incoming' ? 'incoming' : 'outgoing'"
        >
          <v-icon class="header-icon" size="28">
            {{
              lastCallDirection === "incoming"
                ? "mdi-phone-incoming"
                : "mdi-phone-outgoing"
            }}
          </v-icon>
          <div class="icon-ring"></div>
          <div class="icon-ring delay-1"></div>
        </div>
        <div class="header-content">
          <h3 class="header-title">Dispose Call</h3>
          <span class="header-subtitle">
            {{ lastCallDirection === "incoming" ? "Incoming" : "Outgoing" }}
            Call
          </span>
        </div>
        <v-btn
          icon
          small
          class="close-btn"
          :disabled="loading"
          @click="handleCancel"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Call Info Card -->
      <div class="call-info-section">
        <div class="call-info-card">
          <div
            class="caller-avatar"
            :class="lastCallDirection === 'incoming' ? 'incoming' : 'outgoing'"
          >
            <v-icon color="white" size="32">mdi-account</v-icon>
          </div>
          <div class="caller-details">
            <div v-if="callContactName" class="caller-name">
              {{ callContactName }}
            </div>
            <div class="caller-number" :class="{ 'has-name': callContactName }">
              {{ callNumber }}
            </div>
            <div class="call-meta">
              <span class="duration-badge">
                <v-icon x-small class="mr-1">mdi-timer-outline</v-icon>
                {{ formatDuration(callDuration) }}
              </span>
              <span v-if="sipCallId" class="call-id-badge">
                <v-icon x-small class="mr-1">mdi-identifier</v-icon>
                {{ sipCallId.substring(0, 12) }}...
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Disposition Selection -->
      <div class="disposition-body">
        <div class="section-label">
          <v-icon small class="mr-2">mdi-tag-outline</v-icon>
          Select Disposition
        </div>

        <!-- Disposition Grid -->
        <div v-if="dispositionOptions.length > 0" class="disposition-grid">
          <button
            v-for="(dispo, index) in dispositionOptions"
            :key="dispo.value"
            class="disposition-chip"
            :class="{
              selected: selectedDisposition === dispo.value,
              dnc: dispo.value === 'DNC',
              callback: dispo.value === 'CALLBACK',
            }"
            :style="{ animationDelay: `${index * 50}ms` }"
            @click="selectDisposition(dispo.value)"
          >
            <v-icon v-if="getDispoIcon(dispo.value)" small class="chip-icon">
              {{ getDispoIcon(dispo.value) }}
            </v-icon>
            <span class="chip-text">{{ dispo.text }}</span>
            <v-icon
              v-if="selectedDisposition === dispo.value"
              small
              class="check-icon"
            >
              mdi-check-circle
            </v-icon>
          </button>
        </div>

        <transition name="fade-slide">
          <div v-if="dispositionError" class="error-message">
            <v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
            {{ dispositionError }}
          </div>
        </transition>

        <!-- Callback Date/Time Picker -->
        <transition name="fade-slide">
          <div
            v-if="selectedDisposition === 'CALLBACK'"
            class="callback-section"
          >
            <div class="section-label">
              <v-icon small class="mr-2">mdi-calendar-clock</v-icon>
              Schedule Callback
            </div>
            <div class="callback-picker-row">
              <div class="callback-picker-item">
                <v-menu
                  v-model="showDatePicker"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      class="callback-input"
                      :class="{ focused: showDatePicker }"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small class="mr-2">mdi-calendar</v-icon>
                      <span>{{ callbackDate || "Select Date" }}</span>
                    </div>
                  </template>
                  <v-date-picker
                    v-model="callbackDate"
                    :min="minCallbackDate"
                    color="primary"
                    @input="showDatePicker = false"
                  ></v-date-picker>
                </v-menu>
              </div>
              <div class="callback-picker-item">
                <v-menu
                  v-model="showTimePicker"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      class="callback-input"
                      :class="{ focused: showTimePicker }"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small class="mr-2">mdi-clock-outline</v-icon>
                      <span>{{ callbackTime || "Select Time" }}</span>
                    </div>
                  </template>
                  <v-time-picker
                    v-model="callbackTime"
                    format="24hr"
                    color="primary"
                    @click:minute="showTimePicker = false"
                  ></v-time-picker>
                </v-menu>
              </div>
            </div>
          </div>
        </transition>

        <!-- Notes Section -->
        <div class="notes-section">
          <div class="section-label">
            <v-icon small class="mr-2">mdi-note-text-outline</v-icon>
            Notes (Optional)
          </div>
          <div class="notes-input-wrapper" :class="{ focused: notesFocused }">
            <textarea
              v-model="notes"
              class="notes-input"
              placeholder="Add any notes about this call..."
              rows="3"
              @focus="notesFocused = true"
              @blur="notesFocused = false"
            ></textarea>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="disposition-actions">
        <button
          class="action-btn cancel-btn"
          :disabled="loading"
          @click="handleCancel"
        >
          <v-icon small class="mr-2">mdi-close</v-icon>
          Cancel
        </button>
        <button
          class="action-btn dispose-btn"
          :class="{ disabled: !selectedDisposition, loading: loading }"
          :disabled="!selectedDisposition || loading"
          @click="handleDispose"
        >
          <v-progress-circular
            v-if="loading"
            indeterminate
            size="18"
            width="2"
            color="white"
            class="mr-2"
          ></v-progress-circular>
          <v-icon v-else small class="mr-2">mdi-check-circle</v-icon>
          {{ loading ? "Saving..." : "Dispose Call" }}
        </button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import disposer from "@/js/disposer";

export default {
  name: "SipDispositionDialog",
  data() {
    return {
      selectedDisposition: "",
      notes: "",
      loading: false,
      dispositionError: "",
      notesFocused: false,
      callbackDate: "",
      callbackTime: "",
      showDatePicker: false,
      showTimePicker: false,
    };
  },
  computed: {
    ...mapState("dialer", [
      "showDispositionDialog",
      "sipCallId",
      "lastCallDuration",
      "lastCallNumber",
      "lastCallContactName",
      "lastCallContactId",
      "lastCallListId",
      "lastCallDirection",
    ]),
    // Load dispositions from company settings (same as mobile app and web dialer)
    dispositionOptions() {
      const dispoList = this.$store.state.dispoList || [];

      // Filter to show only non-system dispositions or allowed system ones (DNC, CALLBACK)
      const filtered = dispoList.filter((d) => {
        return (
          !d.system ||
          (d.system && d.title === "DNC") ||
          (d.system && d.title === "CALLBACK")
        );
      });

      // Map to dropdown format
      return filtered.map((d) => ({
        text: d.title,
        value: d.title,
      }));
    },
    dialog: {
      get() {
        return this.showDispositionDialog;
      },
      set(value) {
        if (!value) {
          this.handleCancel();
        }
      },
    },
    callDuration() {
      return this.lastCallDuration || 0;
    },
    callNumber() {
      return this.lastCallNumber || "Unknown";
    },
    callContactName() {
      return this.lastCallContactName || "";
    },
    minCallbackDate() {
      // Today's date in YYYY-MM-DD format for date picker min
      return new Date().toISOString().substr(0, 10);
    },
  },
  watch: {
    showDispositionDialog(val, oldVal) {
      console.log(
        `⏱️ [SipDispositionDialog] watch triggered at ${new Date().toISOString()}`,
      );
      console.log(
        `⏱️ [SipDispositionDialog] showDispositionDialog changed from ${oldVal} to ${val}`,
      );
      if (val) {
        // Reset form when dialog opens
        this.selectedDisposition = "";
        this.notes = "";
        this.dispositionError = "";
        this.callbackDate = "";
        this.callbackTime = "";
        this.showDatePicker = false;
        this.showTimePicker = false;
        console.log(
          `⏱️ [SipDispositionDialog] Form reset, dialog should be visible now`,
        );
      }
    },
  },
  methods: {
    formatDuration(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    },

    selectDisposition(value) {
      this.selectedDisposition = value;
      this.dispositionError = "";
      // Reset callback fields when switching away from CALLBACK
      if (value !== "CALLBACK") {
        this.callbackDate = "";
        this.callbackTime = "";
      }
    },

    getDispoIcon(dispoValue) {
      const iconMap = {
        DNC: "mdi-cancel",
        CALLBACK: "mdi-phone-return",
        INTERESTED: "mdi-thumb-up",
        "NOT INTERESTED": "mdi-thumb-down",
        "NO ANSWER": "mdi-phone-missed",
        BUSY: "mdi-phone-off",
        "WRONG NUMBER": "mdi-phone-remove",
        VOICEMAIL: "mdi-voicemail",
        "FOLLOW UP": "mdi-calendar-clock",
        CLOSED: "mdi-check-circle",
        CONVERTED: "mdi-star",
        QUALIFIED: "mdi-check-decagram",
      };
      return iconMap[dispoValue] || null;
    },

    async handleDispose() {
      if (!this.selectedDisposition) {
        this.dispositionError = "Please select a disposition";
        return;
      }

      // Validate callback date/time if CALLBACK is selected
      if (this.selectedDisposition === "CALLBACK") {
        if (!this.callbackDate || !this.callbackTime) {
          this.dispositionError = "Please select callback date and time";
          return;
        }
      }

      this.loading = true;
      this.dispositionError = "";

      try {
        // Find or prepare the contact for disposition
        let contactId = null;
        let contactName = "";
        let contactListId = null;

        const companyId =
          (this.$store.state.companyDetails &&
            this.$store.state.companyDetails.id) ||
          (this.$store.state.user && this.$store.state.user.companyId);

        // Check if we already have a contact ID from auto-dialer (most common case)
        if (this.lastCallContactId) {
          console.log(
            "📍 Using contact ID from auto-dialer:",
            this.lastCallContactId,
            "list:",
            this.lastCallListId
          );
          contactId = this.lastCallContactId;
          contactListId = this.lastCallListId;
          
          // Try to get contact name if we don't have it
          if (!this.lastCallContactName) {
            try {
              const contactResponse = await this.$http.get(
                `${this.$store.state.ENDPOINT}/contacts/${contactId}`
              );
              if (contactResponse.data) {
                contactName = contactResponse.data.name;
              }
            } catch (error) {
              console.warn("⚠️ Could not fetch contact name:", error.message);
              contactName = this.lastCallNumber; // Fallback to phone number
            }
          } else {
            contactName = this.lastCallContactName;
          }
        } else {
          // No contact ID from auto-dialer, need to find or create contact
          console.log("🔍 No contact ID from auto-dialer, searching by phone");
          
          // Normalize phone number for search - try multiple variations
          const phoneRaw = this.lastCallNumber;
          const phoneWithPlus = phoneRaw.startsWith("+")
            ? phoneRaw
            : "+" + phoneRaw;
          const phoneWithoutPlus = phoneRaw.replace(/^\+/, "");
          const phoneClean = phoneRaw.replace(/[\s\-\(\)]/g, ""); // Remove spaces, dashes, parentheses

          // Determine the list ID to filter by (auto-dialer list or selected list)
          const autoDialerListId = this.$store.state.dialer.autoDialerListId;
          const selectedList = this.$store.state.selectedList;
          const filterListId = autoDialerListId || this.lastCallListId || selectedList;
          console.log("🔍 Filter list ID for phone search:", filterListId, "(autoDialer:", autoDialerListId, "lastCall:", this.lastCallListId, "selected:", selectedList, ")");

          try {
            // Search for contact with phone variations using OR query
            // CRITICAL: Also filter by listId to avoid finding contacts in other lists
            var searchConditions = [
              { companyId: companyId },
              {
                or: [
                  { phone: phoneRaw },
                  { phone: phoneWithPlus },
                  { phone: phoneWithoutPlus },
                  { phone: phoneClean },
                ],
              },
            ];
            // Add list ID filter if available
            if (filterListId) {
              searchConditions.push({ listId: filterListId });
            }
            const searchFilter = {
              where: {
                and: searchConditions,
              },
              limit: 1,
            };

            console.log(
              "🔍 Searching for contact with filter:",
              JSON.stringify(searchFilter)
            );

            const contactResponse = await this.$http.get(
              `${this.$store.state.ENDPOINT}/contacts?filter=${encodeURIComponent(
                JSON.stringify(searchFilter)
              )}`
            );

            if (contactResponse.data && contactResponse.data.length > 0) {
              const found = contactResponse.data[0];
              contactId = found.id;
              contactName = found.name || this.lastCallNumber;
              contactListId = found.listId;
              console.log("📇 Found existing contact by phone:", contactId);
            }
          } catch (error) {
            console.log("⚠️ Could not find existing contact:", error.message);
          }
        }

        // If no contact found, create a new one
        if (!contactId) {
          // First, check if we have a list ID from auto-dialer
          let listId = this.lastCallListId || null;

          if (listId) {
            console.log("📋 Using list ID from auto-dialer:", listId);
          } else {
            // Otherwise, try to get the default calling list from PBX config
            try {
              // Fetch PBX config to get defaultCallingListId
              const pbxResponse = await this.$http.get(
                `${this.$store.state.ENDPOINT}/pbx?filter=${encodeURIComponent(
                  JSON.stringify({
                    where: { companyId: companyId },
                    limit: 1,
                  }),
                )}`,
              );

              if (pbxResponse.data && pbxResponse.data.length > 0) {
                const pbxConfig = pbxResponse.data[0];
                if (pbxConfig.defaultCallingListId) {
                  listId = pbxConfig.defaultCallingListId;
                  console.log("📋 Using PBX default calling list:", listId);
                }
              }
            } catch (error) {
              console.log("⚠️ Could not fetch PBX config:", error.message);
            }
          }

          // If still no list ID, fall back to first available list
          if (!listId) {
            try {
              console.log(
                "⚠️ No default calling list in PBX config, using first available list",
              );
              const listsResponse = await this.$http.get(
                `${
                  this.$store.state.ENDPOINT
                }/lists?filter=${encodeURIComponent(
                  JSON.stringify({
                    where: { companyId: companyId },
                    limit: 1,
                    order: "createdOn DESC",
                  }),
                )}`,
              );

              if (listsResponse.data && listsResponse.data.length > 0) {
                listId = listsResponse.data[0].id;
                console.log("📋 Using fallback list:", listId);
              } else {
                throw new Error("No lists found for company");
              }
            } catch (error) {
              console.error("❌ Error finding list:", error);
              throw new Error(
                "Cannot create contact: No lists available. Please create a list first.",
              );
            }
          }

          const newContactData = {
            phone: this.lastCallNumber,
            name: this.lastCallNumber, // Use phone as name initially
            companyId: companyId,
            accountsId: this.$store.state.user && this.$store.state.user.id,
            listId: listId, // CRITICAL: Must have list ID
            called: false, // Will be set to true during disposal
            dispo: "NEW",
            customFields: {},
          };

          console.log("✨ Creating new contact:", newContactData);

          try {
            const createResponse = await this.$http.post(
              `${this.$store.state.ENDPOINT}/contacts`,
              newContactData,
            );

            contactId = createResponse.data.id;
            contactName = createResponse.data.name || this.lastCallNumber;
            contactListId = createResponse.data.listId;
            console.log("✅ Created new contact:", contactId);
          } catch (createErr) {
            // Handle duplicate key error — contact already exists in this list
            const errBody = createErr.body || createErr.data || {};
            const errMsg = (errBody.error && errBody.error.message) || createErr.message || "";
            if (errMsg.includes("E11000") || errMsg.includes("duplicate key")) {
              console.log("⚠️ Contact already exists (duplicate key), finding existing one...");
              try {
                const dupFilter = {
                  where: {
                    and: [
                      { companyId: companyId },
                      { listId: listId },
                      { phone: this.lastCallNumber },
                    ],
                  },
                  limit: 1,
                };
                const dupResponse = await this.$http.get(
                  `${this.$store.state.ENDPOINT}/contacts?filter=${encodeURIComponent(
                    JSON.stringify(dupFilter)
                  )}`
                );
                if (dupResponse.data && dupResponse.data.length > 0) {
                  const existing = dupResponse.data[0];
                  contactId = existing.id;
                  contactName = existing.name || this.lastCallNumber;
                  contactListId = existing.listId;
                  console.log("📇 Found existing contact after dup error:", contactId);
                } else {
                  throw new Error("Contact exists but could not be found");
                }
              } catch (findErr) {
                console.error("❌ Failed to find existing contact:", findErr);
                throw createErr; // Re-throw original error
              }
            } else {
              throw createErr; // Not a duplicate key error, re-throw
            }
          }
        }

        // Now create call item with the contact ID
        const now = new Date();
        const callStart = new Date(
          now.getTime() - this.lastCallDuration * 1000,
        );

        // Use lastCallListId from auto-dialer if available, otherwise use contact's listId
        const effectiveListId = this.lastCallListId || contactListId;

        const callItem = {
          id: contactId, //CRITICAL: Must have contact ID for disposal
          phone: this.lastCallNumber,
          name: contactName || this.lastCallNumber,
          companyId: companyId,
          accountsId: this.$store.state.user && this.$store.state.user.id,
          listId: effectiveListId, // Use list ID from auto-dialer or contact
          called: true,
          dispo: this.selectedDisposition,
          notes: this.notes,
          remarks: this.notes,
          duration: this.lastCallDuration * 1000, // Convert to milliseconds
          timeSpentOnCall: this.lastCallDuration * 1000,
          callStart: callStart,
          calledOn: callStart, // Some parts of the code expect calledOn
          modifiedOn: now,
          direction: this.lastCallDirection || "outgoing", // Use stored direction
          // Handle DNC if selected
          dnc: this.selectedDisposition === "DNC",
          // Handle CALLBACK if selected
          callback: this.selectedDisposition === "CALLBACK",
          callbackDone: this.selectedDisposition !== "CALLBACK",
          // Add callback date/time if CALLBACK is selected
          callbackOn:
            this.selectedDisposition === "CALLBACK" &&
            this.callbackDate &&
            this.callbackTime
              ? new Date(
                  `${this.callbackDate}T${this.callbackTime}:00`,
                ).toISOString()
              : null,
        };

        console.log("📞 Disposing SIP call with item:", callItem);
        console.log("📍 Call direction:", this.lastCallDirection || "outgoing");
        console.log("📋 Using list ID:", effectiveListId, "(from auto-dialer:", this.lastCallListId, ", from contact:", contactListId, ")");
        console.log("🎯 [CRITICAL] Disposing contact ID:", contactId, "- This contact should be marked as called=true in database");

        // Dispose call with SIP Call-ID
        const response = await disposer.setDisposition(
          callItem,
          false, // returnNextCall
          this.sipCallId,
        );

        console.log("✅ Call disposed successfully:", response.data);
        console.log("✅ [CRITICAL] Response contact status:", {
          contactId: response.data && response.data.contact && response.data.contact.id,
          called: response.data && response.data.contact && response.data.contact.called,
          dispo: response.data && response.data.contact && response.data.contact.dispo,
        });

        if (response.data && response.data.sipRecordingLinked) {
          console.log(
            "📹 SIP recording linked:",
            response.data.sipRecordingLinked,
          );
          this.$emit("recording-linked", response.data.sipRecordingLinked);
        }

        // Show success message
        this.$swal({
          icon: "success",
          title: "Call disposed successfully",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });

        // Close dialog and clear data
        this.$store.commit("dialer/SET_SHOW_DISPOSITION_DIALOG", false);
        this.$store.commit("dialer/CLEAR_SIP_CALL_DATA");
      } catch (error) {
        console.error("❌ Disposal failed:", error);
        this.dispositionError = error.message || "Failed to dispose call";

        this.$swal({
          icon: "error",
          title: "Failed to dispose call",
          text: error.message,
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      } finally {
        this.loading = false;
      }
    },

    handleCancel() {
      if (!this.loading) {
        this.$store.commit("dialer/SET_SHOW_DISPOSITION_DIALOG", false);
        this.$store.commit("dialer/CLEAR_SIP_CALL_DATA");
        this.selectedDisposition = "";
        this.notes = "";
        this.dispositionError = "";
      }
    },
  },
};
</script>

<style scoped>
/* ===== Base Card Styles ===== */
.disposition-card {
  border-radius: 28px !important;
  overflow: hidden;
  position: relative;
  background: #ffffff;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
}

.disposition-card.incoming-call {
  box-shadow: 0 25px 50px -12px rgba(76, 175, 80, 0.2) !important;
}

/* ===== Animated Background Gradient ===== */
.disposition-bg-gradient {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 140px;
  z-index: 0;
  transition: all 0.5s ease;
}

.disposition-bg-gradient.outgoing {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.disposition-bg-gradient.incoming {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
}

/* ===== Header Section ===== */
.disposition-header {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  padding: 20px 20px 16px;
  color: white;
}

.header-icon-container {
  position: relative;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

.header-icon-container.outgoing {
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.header-icon-container.incoming {
  box-shadow: 0 4px 15px rgba(17, 153, 142, 0.3);
}

.header-icon {
  color: white !important;
}

.icon-ring {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  animation: ringPulse 2s ease-out infinite;
}

.icon-ring.delay-1 {
  animation-delay: 0.5s;
}

.header-content {
  flex: 1;
}

.header-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  letter-spacing: 0.3px;
}

.header-subtitle {
  font-size: 13px;
  opacity: 0.85;
  font-weight: 500;
}

.close-btn {
  color: rgba(255, 255, 255, 0.8) !important;
  transition: all 0.2s ease;
}

.close-btn:hover {
  color: white !important;
  background: rgba(255, 255, 255, 0.15) !important;
}

/* ===== Call Info Section ===== */
.call-info-section {
  position: relative;
  z-index: 1;
  padding: 0 20px;
  margin-top: -10px;
}

.call-info-card {
  display: flex;
  align-items: center;
  background: white;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.caller-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  flex-shrink: 0;
}

.caller-avatar.outgoing {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.caller-avatar.incoming {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
}

.caller-details {
  flex: 1;
  min-width: 0;
}

.caller-name {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a2e;
  letter-spacing: 0.3px;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.caller-number {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.caller-number.has-name {
  font-size: 14px;
  font-weight: 500;
  color: #666;
  letter-spacing: 0.3px;
  margin-bottom: 6px;
}
.call-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.duration-badge,
.call-id-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.duration-badge {
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.1) 0%,
    rgba(118, 75, 162, 0.1) 100%
  );
  color: #667eea;
}

.call-id-badge {
  background: #f5f5f5;
  color: #757575;
}

/* ===== Disposition Body ===== */
.disposition-body {
  position: relative;
  z-index: 1;
  padding: 20px;
  background: white;
  /* Enable scrolling when content overflows */
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  max-height: 50vh; /* Limit body height to enable scrolling */
  /* Custom scrollbar styling */
  scrollbar-width: thin;
  scrollbar-color: rgba(102, 126, 234, 0.3) transparent;
}

/* Webkit scrollbar styling for Chrome/Safari */
.disposition-body::-webkit-scrollbar {
  width: 6px;
}

.disposition-body::-webkit-scrollbar-track {
  background: transparent;
  margin: 8px 0;
}

.disposition-body::-webkit-scrollbar-thumb {
  background: rgba(102, 126, 234, 0.3);
  border-radius: 3px;
}

.disposition-body::-webkit-scrollbar-thumb:hover {
  background: rgba(102, 126, 234, 0.5);
}

.section-label {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #757575;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ===== Disposition Grid ===== */
.disposition-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
  /* Allow grid to grow naturally and let parent (disposition-body) handle scrolling */
}

.disposition-chip {
  display: inline-flex;
  align-items: center;
  padding: 10px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background: white;
  font-size: 13px;
  font-weight: 600;
  color: #424242;
  cursor: pointer;
  transition: all 0.2s ease;
  animation: chipAppear 0.3s ease backwards;
}

.disposition-chip:hover {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.05);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.disposition-chip.selected {
  border-color: #667eea;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.disposition-chip.dnc {
  border-color: #ffcdd2;
}

.disposition-chip.dnc:hover {
  border-color: #ef5350;
  background: rgba(239, 83, 80, 0.05);
}

.disposition-chip.dnc.selected {
  background: linear-gradient(135deg, #ef5350 0%, #e53935 100%);
  border-color: #ef5350;
  box-shadow: 0 4px 15px rgba(239, 83, 80, 0.4);
}

.disposition-chip.callback {
  border-color: #c8e6c9;
}

.disposition-chip.callback:hover {
  border-color: #66bb6a;
  background: rgba(102, 187, 106, 0.05);
}

.disposition-chip.callback.selected {
  background: linear-gradient(135deg, #66bb6a 0%, #43a047 100%);
  border-color: #66bb6a;
  box-shadow: 0 4px 15px rgba(102, 187, 106, 0.4);
}

.chip-icon {
  margin-right: 6px;
  opacity: 0.8;
}

.disposition-chip.selected .chip-icon {
  opacity: 1;
}

.check-icon {
  margin-left: 6px;
  animation: checkPop 0.3s ease;
}

/* ===== Error Message ===== */
.error-message {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  background: #ffebee;
  border-radius: 10px;
  color: #c62828;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 16px;
}

/* ===== Callback Section ===== */
.callback-section {
  margin-top: 16px;
  margin-bottom: 8px;
}

.callback-picker-row {
  display: flex;
  gap: 12px;
  margin-top: 12px;
}

.callback-picker-item {
  flex: 1;
}

.callback-input {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  font-size: 14px;
  color: #1a1a2e;
}

.callback-input:hover {
  border-color: #667eea;
  background: #f8f9ff;
}

.callback-input.focused {
  border-color: #667eea;
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
}

.callback-input span {
  color: #666;
}

.callback-input .v-icon {
  color: #667eea;
}

/* ===== Notes Section ===== */
.notes-section {
  margin-top: 8px;
}

.notes-input-wrapper {
  border: 2px solid #e0e0e0;
  border-radius: 14px;
  transition: all 0.2s ease;
  overflow: hidden;
}

.notes-input-wrapper.focused {
  border-color: #667eea;
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
}

.notes-input {
  width: 100%;
  border: none;
  outline: none;
  padding: 14px 16px;
  font-size: 14px;
  font-family: inherit;
  color: #1a1a2e;
  resize: none;
  background: transparent;
}

.notes-input::placeholder {
  color: #9e9e9e;
}

/* ===== Action Buttons - Base styles only, layout in scrollable section ===== */
.action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 20px;
  border: none;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn {
  background: #f5f5f5;
  color: #757575;
}

.cancel-btn:hover:not(:disabled) {
  background: #eeeeee;
  color: #424242;
}

.cancel-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.dispose-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.dispose-btn:hover:not(:disabled):not(.loading) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
}

.dispose-btn.disabled {
  background: #e0e0e0;
  color: #9e9e9e;
  box-shadow: none;
  cursor: not-allowed;
}

.dispose-btn.loading {
  cursor: wait;
}

/* ===== Animations ===== */
@keyframes ringPulse {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  100% {
    transform: scale(1.4);
    opacity: 0;
  }
}

@keyframes chipAppear {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes checkPop {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

/* ===== Vue Transitions ===== */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* ===== Dark Mode Support ===== */
@media (prefers-color-scheme: dark) {
  .disposition-card {
    background: #1a1a2e;
  }

  .call-info-card {
    background: #252542;
  }

  .caller-number {
    color: #ffffff;
  }

  .disposition-body {
    background: #1a1a2e;
    scrollbar-color: rgba(102, 126, 234, 0.4) transparent;
  }

  .disposition-body::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.4);
  }

  .disposition-body::-webkit-scrollbar-thumb:hover {
    background: rgba(102, 126, 234, 0.6);
  }

  .section-label {
    color: #9e9e9e;
  }

  .disposition-chip {
    background: #252542;
    border-color: #3d3d6b;
    color: #ffffff;
  }

  .disposition-chip:hover {
    background: rgba(102, 126, 234, 0.15);
  }

  .notes-input-wrapper {
    border-color: #3d3d6b;
    background: #252542;
  }

  .notes-input {
    color: #ffffff;
  }

  .disposition-actions {
    background: #1a1a2e;
  }

  .cancel-btn {
    background: #252542;
    color: #9e9e9e;
  }

  .cancel-btn:hover:not(:disabled) {
    background: #3d3d6b;
    color: #ffffff;
  }
}

/* ===== Responsive ===== */
@media (max-width: 420px) {
  .disposition-card {
    border-radius: 20px !important;
  }

  .caller-number {
    font-size: 18px;
  }

  .disposition-chip {
    padding: 8px 12px;
    font-size: 12px;
  }

  .disposition-body {
    max-height: 40vh; /* Smaller max height on mobile */
    padding: 16px;
  }
}

/* ===== Mobile Scrollable Dialog - Always apply for proper scroll behavior ===== */
.disposition-card {
  max-height: calc(100vh - 48px); /* Account for browser chrome */
  max-height: calc(
    100dvh - 48px
  ); /* Dynamic viewport height for mobile browsers */
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.disposition-header {
  flex-shrink: 0;
}

.call-info-section {
  flex-shrink: 0;
}

.disposition-actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
  position: relative;
  z-index: 10;
  background: white;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
  /* Ensure buttons are always visible */
  padding: 16px 20px 24px;
  /* Safe area for mobile devices with notches/home indicators */
  padding-bottom: max(24px, env(safe-area-inset-bottom, 24px));
}

/* ===== Mobile-specific adjustments ===== */
@media (max-height: 700px), (max-width: 480px) {
  .disposition-card {
    max-height: calc(100vh - 32px);
    max-height: calc(100dvh - 32px);
  }

  .disposition-bg-gradient {
    height: 120px;
  }

  .header-icon-container {
    width: 40px;
    height: 40px;
  }

  .header-icon {
    font-size: 22px !important;
  }

  .header-title {
    font-size: 16px;
  }

  .caller-avatar {
    width: 48px;
    height: 48px;
  }

  .caller-name {
    font-size: 16px;
  }

  .caller-number {
    font-size: 18px;
  }

  .disposition-body {
    max-height: 35vh; /* Smaller on mobile */
    padding: 16px;
  }

  .disposition-grid {
    gap: 8px; /* Tighter spacing on mobile */
  }

  .disposition-chip {
    padding: 8px 12px;
    font-size: 12px;
  }

  .callback-picker-row {
    flex-direction: column;
    gap: 8px;
  }

  .notes-input {
    min-height: 60px;
  }

  .disposition-actions {
    padding: 12px 16px 20px;
    padding-bottom: max(20px, env(safe-area-inset-bottom, 20px));
  }
}

/* ===== Extra small screens ===== */
@media (max-height: 550px) {
  .disposition-card {
    max-height: calc(100vh - 24px);
    max-height: calc(100dvh - 24px);
  }

  .disposition-header {
    padding: 12px 16px 10px;
  }

  .call-info-section {
    padding: 0 16px;
  }

  .call-info-card {
    padding: 12px;
  }

  .disposition-body {
    padding: 12px 16px;
    max-height: 30vh; /* Very limited height on small screens */
  }

  .disposition-actions {
    padding: 10px 16px 16px;
    padding-bottom: max(16px, env(safe-area-inset-bottom, 16px));
  }

  .section-label {
    margin-bottom: 8px;
    font-size: 11px;
  }

  .disposition-chip {
    padding: 6px 10px;
    font-size: 11px;
  }

  .disposition-grid {
    gap: 6px; /* Tightest spacing on very small screens */
  }
}
</style>

<!-- Global styles for dialog wrapper -->
<style>
.v-dialog.disposition-dialog {
  border-radius: 28px !important;
  overflow: hidden !important;
  margin: 24px !important;
  max-height: calc(100% - 48px) !important;
}

.v-dialog.disposition-dialog > .v-card {
  border-radius: 28px !important;
  max-height: 100% !important;
}

/* Ensure dialog is properly positioned on mobile */
@media (max-width: 480px), (max-height: 700px) {
  .v-dialog.disposition-dialog {
    margin: 16px !important;
    max-height: calc(100% - 32px) !important;
    border-radius: 20px !important;
  }

  .v-dialog.disposition-dialog > .v-card {
    border-radius: 20px !important;
  }
}

/* Extra small screens - minimal margins */
@media (max-height: 550px) {
  .v-dialog.disposition-dialog {
    margin: 12px !important;
    max-height: calc(100% - 24px) !important;
  }
}
</style>
